import { PLAY_TRACK_PATTERN } from '@hangar/shared';

/**
 * The submit settings of one environment of a profile (P29, SPEC §39.4): the console's equivalent of an EAS submit profile.
 * Pure: the form state, its checks and the body the API takes. The store app is picked next to them, in the binding.
 */

export type ReleaseStatus = 'completed' | 'draft' | 'inProgress';

export interface SubmitForm {
  autoSubmit: boolean;
  track: string;
  releaseStatus: ReleaseStatus;
  /** The staged rollout share as typed (1–99, percent). */
  rolloutPercent: string;
  changesNotSentForReview: boolean;
}

export interface SavedSubmit {
  autoSubmit: boolean;
  track: string;
  releaseStatus: string;
  rolloutFraction?: number | null;
  changesNotSentForReview?: boolean;
}

/** What a binding starts with when someone turns submission on: Internal testing, released at once. */
export const NEW_SUBMIT: SubmitForm = { autoSubmit: false, track: 'internal', releaseStatus: 'completed', rolloutPercent: '10', changesNotSentForReview: false };

export const RELEASE_STATUS_OPTIONS: { value: ReleaseStatus; label: string; description: string }[] = [
  { value: 'completed', label: 'Release', description: 'Released on the track as soon as Google Play accepts it' },
  { value: 'draft', label: 'Draft', description: 'Waits in Play Console until someone releases it there' },
  { value: 'inProgress', label: 'Staged', description: 'Released to a share of the track\'s users first' },
];

/** The saved settings as form state, or null when the environment does not submit. */
export function submitFormOf(saved: SavedSubmit | null | undefined): SubmitForm | null {
  if (!saved) return null;
  const status: ReleaseStatus = saved.releaseStatus === 'draft' || saved.releaseStatus === 'inProgress' ? saved.releaseStatus : 'completed';
  return {
    autoSubmit: saved.autoSubmit,
    track: saved.track,
    releaseStatus: status,
    rolloutPercent: saved.rolloutFraction ? String(Math.round(saved.rolloutFraction * 100)) : NEW_SUBMIT.rolloutPercent,
    changesNotSentForReview: saved.changesNotSentForReview === true,
  };
}

/** What is wrong with the settings, in one line, or undefined. */
export function submitError(s: SubmitForm): string | undefined {
  const track = s.track.trim();
  if (!track) return 'Enter the Google Play track, such as internal';
  if (!PLAY_TRACK_PATTERN.test(track)) return 'A track id is lowercase letters, digits, ":", "_" and "-" (internal, alpha, beta, production, pre-prod)';
  if (s.releaseStatus === 'inProgress') {
    const pct = Number(s.rolloutPercent);
    if (!Number.isInteger(pct) || pct < 1 || pct > 99) return 'A staged rollout goes to 1–99 % of users';
  }
  return undefined;
}

/** The API's `submit` of a binding (no fraction unless a staged rollout). Only call it on settings without an error. */
export function submitPayloadOf(s: SubmitForm): SavedSubmit & { releaseStatus: ReleaseStatus; changesNotSentForReview: boolean } {
  return {
    autoSubmit: s.autoSubmit,
    track: s.track.trim(),
    releaseStatus: s.releaseStatus,
    changesNotSentForReview: s.changesNotSentForReview,
    ...(s.releaseStatus === 'inProgress' ? { rolloutFraction: Number(s.rolloutPercent) / 100 } : {}),
  };
}

/** One line for a binding's summary: "Internal testing · released · after each build". */
export function submitSummary(s: SubmitForm): string {
  const track: Record<string, string> = { internal: 'Internal testing', alpha: 'Closed testing (alpha)', beta: 'Open testing (beta)', production: 'Production' };
  const status = s.releaseStatus === 'inProgress' ? `staged to ${s.rolloutPercent} %` : s.releaseStatus === 'draft' ? 'as a draft' : 'released';
  return `${track[s.track.trim()] ?? s.track.trim()} · ${status} · ${s.autoSubmit ? 'after each build' : 'when someone submits'}`;
}
